//MainMap.ts =================================================
import "./Util/Prototypes";

import * as BasemapGallery from "./Widgets/BasemapGallery";
import * as CodedValueDomains from "./Util/CodedValueDomains";
import * as CompareMaps from "./Widgets/CompareMaps";
import * as Custom from "./Custom";
import * as DrawerMenu from "./Menus/DrawerMenu";
import * as ElevationProfile from "./Widgets/ElevationProfile";
import * as FeatureLayerLabels from "./Util/FeatureLayerLabels";
import * as GraLay from "./Util/GraLay";
import * as Highlight from "./Util/Highlight";
import * as HighlightGraphics from "./Util/HighlightGraphics";
import * as Legend from "./Widgets/Legend";
import * as MainMap from "./MainMap";
import * as MainToolbar from "./Menus/MainToolbar";
import * as MapLayers from "./Widgets/MapLayers";
import * as MapThemes from "./Widgets/MapThemes";
import * as MapTips from "./Util/MapTips";
import * as PrevNextExtent from "./Menus/PrevNextExtent";
import * as QueryString from "./QueryString";
import * as ScaleDependentRender from "./Util/ScaleDependentRender";
import * as SelectOnMap from "./Search/SelectOnMap";
import * as Settings from "./Settings";
import * as Spinner from "./Util/Spinner";
import * as Util from "./Util/Util";
import * as Util_App from "./Util/Util_App";
import * as Widgets from "./Widgets/Widgets";

//ESRI javascript API.
import Basemap from "@arcgis/core/Basemap";
import ElevationLayer from "@arcgis/core/layers/ElevationLayer";
import esriConfig from "@arcgis/core/config";
esriConfig.request.useIdentity = false; // Recommended by ESRI to disable the authentication code.

import Extent from "@arcgis/core/geometry/Extent";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import GeoRSSLayer from "@arcgis/core/layers/GeoRSSLayer";
import Ground from "@arcgis/core/Ground";
import GroupLayer from "@arcgis/core/layers/GroupLayer";
import ImageryLayer from "@arcgis/core/layers/ImageryLayer";
import ImageryTileLayer from "@arcgis/core/layers/ImageryTileLayer";
import IntegratedMeshLayer from "@arcgis/core/layers/IntegratedMeshLayer";
import KMLLayer from "@arcgis/core/layers/KMLLayer";
import Map from "@arcgis/core/Map";
import MapImageLayer from "@arcgis/core/layers/MapImageLayer";
import MapView from "@arcgis/core/views/MapView";
import PointCloudLayer from "@arcgis/core/layers/PointCloudLayer";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import SceneLayer from "@arcgis/core/layers/SceneLayer";
import TileLayer from "@arcgis/core/layers/TileLayer";
import * as urlUtils from "@arcgis/core/core/urlUtils";
import VectorTileLayer from "@arcgis/core/layers/VectorTileLayer";
import WMSLayer from "@arcgis/core/layers/WMSLayer";
import WMTSLayer from "@arcgis/core/layers/WMTSLayer";

type mapImageFormat = "png" | "png8" | "png24" | "png32" | "jpg" | "pdf" | "bmp" | "gif" | "svg" | "pngjpg";
type imageryLayerFormat = "png" | "png8" | "png24" | "png32" | "jpg" | "bmp" | "gif" | "jpgpng" | "lerc" | "tiff";

export let map: __esri.Map;
export let mapView: __esri.MapView;
export let sceneView: __esri.SceneView;
export let activeView: __esri.MapView | __esri.SceneView;
export let view2D: boolean = true;

export let basemaps: __esri.Basemap[]; //Exported for use in base maps gallery.
export let startUpInProgress: boolean;
export let reBuildMapInProgress: boolean;

export let mapExtentFull: __esri.Extent;

let mapExtentStart: __esri.Extent;
let mapExtentQS: __esri.Extent;

let addElevationLayers: __esri.ElevationLayer[]; //Global elevation layers to append to the world elevation layer.
let graphicsLayers: __esri.GraphicsLayer[];
let readyForRefresh2D: boolean = false;
const viewDiv1 = document.getElementById("viewDiv1") as HTMLDivElement;
const viewDiv2 = document.getElementById("viewDiv2") as HTMLDivElement;
export let activeViewDiv: HTMLDivElement = viewDiv1;
let oldViewDiv: HTMLDivElement = viewDiv2;
let startupBasemap: __esri.Basemap;
let mapTheme: Settings.iMapTheme;
let busy: boolean = true;

//Build all esri components.
export function build() {

   startUpInProgress = true;
   initMapExtents();

   //No 'Access-Control-Allow-Origin' header is present on the requested resource.
   //Origin 'http://localhost:65218' is therefore not allowed access. The response had HTTP status code 500.

   Util.logMsg("Setting proxy URL, portal URL's and CORS enabled servers.");

   //Portal URL.
   if (Settings.portalUrl) {
      esriConfig.portalUrl = Settings.portalUrl;
   }

   //Proxy URL and rules.
   if (Settings.proxyUrl && Settings.proxyMapServiceURLs.length > 0) {
      esriConfig.request.proxyUrl = Settings.proxyUrl;

      for (const proxyRule of Settings.proxyMapServiceURLs) {
         urlUtils.addProxyRule({ proxyUrl: Settings.proxyUrl, urlPrefix: proxyRule });
      }
   }

   //CORS enabled servers.
   for (const corServer of Settings.corsEnabledServers) {
      esriConfig.request.trustedServers.push(corServer);
   }

   buildMap();
}

//Build only the map components on startup and when 2D/3D toggle.
function buildMap(mapExtent?: __esri.Extent) {
   reBuildMapInProgress = true;

   //Don't show tools that only work in 2D.
   if (mapExtent) {
      const ok: boolean = DrawerMenu.isValidTool3D(DrawerMenu.prevToolName);
      if (!ok) {
         DrawerMenu.initTool("Quick Search");
      }
   }

   //Get startup map theme setting.
   mapTheme = MapThemes.getMapTheme(MapThemes.activeMapTheme);

   if (!MainMap.activeView) { //Keep previous map theme when 2D/3D toggle.
      if (QueryString.mapTheme) {
         mapTheme = MapThemes.getMapTheme(QueryString.mapTheme);
         QueryString.setMapTheme(null);

      } else if (localStorage.Exists(MapLayers.themeName)) {
         mapTheme = localStorage.GetItem(MapLayers.themeName) as Settings.iMapTheme;
      }
   }

   //Load map services.
   if (!MainMap.basemaps) {
      basemaps = loadBasemaps(); // Required for basemap gallery widget.
   }

   const operationalLayers = loadOperationalLayers(); //Reload to filter out slow layers in 3D.

   if (!graphicsLayers) {
      graphicsLayers = GraLay.loadGraphicsLayers();
   }

   //Create map object.
   Util.logMsg('Creating ags.Map');

   //Set startup basemap.
   if (MainMap.activeView) {
      startupBasemap = MainMap.map.basemap; //Keep previous basemap when 2D/3D toggle.

   } else {
      //Set startup basemap.
      startupBasemap = MainMap.basemaps[MainMap.basemaps.length - 1];

      for (const bm of MainMap.basemaps) {
         const bmFound: boolean = MapThemes.mapServiceExistInMapTheme(mapTheme, bm.id);
         if (bmFound) {
            startupBasemap = bm;
            break;
         }
      }
   }

   //Create main map.
   map = new Map({
      basemap: startupBasemap,
      layers: operationalLayers,
   });

   //Set the ground terrain.
   MainMap.map.ground = loadGround() as __esri.Ground;

   //Append additional global elevation layers if any specified.
   if (addElevationLayers) {
      for (const elevLay of addElevationLayers) {
         MainMap.map.ground.layers.add(elevLay);
      }
   }

   //Create map or scene view.
   if (MainMap.view2D) {
      createMapView(mapExtent);
   } else {
      createSceneView(mapExtent);
   }

   //Set flags to reload items after creating map view or scene view.
   CompareMaps.setReloadBasemaps(true);
   SelectOnMap.setCreateSketchVM(true);

   //Initialize prev next buttons.
   if (MainMap.startUpInProgress) {
      PrevNextExtent.render();
   }
}

//Load base maps.
function loadBasemaps(): __esri.Basemap[] {
   const basemaps2: __esri.Basemap[] = [];
   let basemap: __esri.Basemap;
   let baseLayer: __esri.Layer;
   let props: __esri.FeatureLayerProperties;
   let opacity: number;
   let legendLayer: Settings.iLegendLayer;

   for (const bm of Settings.basemaps) {

      opacity = MapThemes.getMapThemeOpacity(mapTheme, bm.id); //Check startup map theme for opacity.
      if (opacity === -1) {
         opacity = null;
         if (bm.opacity !== 1) {
            opacity = bm.opacity; //Use opacity from the settings.
         }
      }

      props = {
         id: bm.id,
         opacity: opacity,
         url: bm.mapServiceURL,
         visible: true,
      };

      //Use portal item instead of url.
      if (!bm.mapServiceURL.toLowerCase().StartsWith("http")) {
         delete props.url;
         props.portalItem = { id: bm.mapServiceURL };
      }

      //Hide in legend if setting found.
      if (!bm.serviceType.Equal("VectorTileLayer")) {
         legendLayer = Legend.getLegendLayerSetting(bm.id);
         if (legendLayer) {
            props.legendEnabled = false;
         }
      }

      //Process all map service types.
      if (bm.serviceType.Equal("TileLayer")) {
         const tileLayerProps = props as __esri.TileLayerProperties;
         baseLayer = new TileLayer(tileLayerProps);

      } else if (bm.serviceType.Equal("MapImageLayer")) {
         const mapImageLayerProps = props as __esri.MapImageLayerProperties;

         if (bm.imageFormat) {
            mapImageLayerProps.imageFormat = bm.imageFormat as mapImageFormat;
         }
         baseLayer = new MapImageLayer(mapImageLayerProps);

      } else if (bm.serviceType.Equal("ImageryLayer")) {
         const imageryLayerProps = props as __esri.ImageryLayerProperties;

         if (bm.imageFormat) {
            imageryLayerProps.format = bm.imageFormat as imageryLayerFormat;
         }
         baseLayer = new ImageryLayer(imageryLayerProps);

      } else if (bm.serviceType.Equal("ImageryTileLayer")) {
         const imageryTileLayerProps = props as __esri.ImageryTileLayerProperties;
         baseLayer = new ImageryTileLayer(imageryTileLayerProps);

      } else if (bm.serviceType.Equal("VectorTileLayer")) {
         const vectorTileLayerProps = props as __esri.VectorTileLayerProperties;
         baseLayer = new VectorTileLayer(vectorTileLayerProps);

      } else if (bm.serviceType.Equal("WMSLayer")) {
         const wmsLayerProps = props as __esri.WMSLayerProperties;
         baseLayer = new WMSLayer(wmsLayerProps);

      } else if (bm.serviceType.Equal("WMTSLayer")) {
         const wmtsLayerProps = props as __esri.WMTSLayerProperties;
         baseLayer = new WMTSLayer(wmtsLayerProps);
      }

      //Set custom layer properties.
      Custom.setLayerProps(baseLayer);

      basemap = new Basemap({
         id: bm.id,
         title: bm.id,
         baseLayers: [baseLayer],
         thumbnailUrl: bm.thumbnail,
      });

      Util.logMsg('Adding basemap layer: "' + basemap.id + '"');
      basemaps2.Add(basemap);

      //Set spatial reference and build sub layers.
      baseLayer.when(function (layer: __esri.Layer) {
         Util.logMsg('Basemap layer is ready: "' + layer.id + '"');

         //Add spatial reference to basemaps array.
         for (const basemap3 of basemaps2) {
            if (basemap3.id === layer.id) {
               if (layer.type !== "wmts") { // wmts causes issues with Basemap Gallery.
                  const mapImageLayer = layer as __esri.MapImageLayer; //Hacky.
                  basemap3.spatialReference = mapImageLayer.spatialReference;
               }
            }
         }

         //Process sub layers in map image service.
         if (layer.type === "map-image") {
            const mapImageLayer = layer as __esri.MapImageLayer;

            if (mapImageLayer.sublayers && mapImageLayer.sublayers.length > 0) {
               Util_App.setSublayerIndexesInSettings(mapImageLayer); //Set sublayer id's in settings.
            }
         }

      }).catch(function (error: Error) { //Trap for errors.
         Util.errMsg("Basemap layer failed to load.", error);
      });
   }

   return basemaps2;
}

//Load operational layers.
function loadOperationalLayers(): __esri.Layer[] {
   const operLayers: __esri.Layer[] = [];
   let operLayer: __esri.Layer;
   let props: __esri.FeatureLayerProperties;
   let subLayersOn: Settings.iSublayer[];
   let visible: boolean;
   let opacity: number;
   let legendLayer: Settings.iLegendLayer;

   let groupLayerInProgress: boolean = false;
   let groupLay: __esri.GroupLayer;

   //Process all operational maps.
   for (const om of Settings.operMaps) {

      if (MainMap.view2D) {
         //Skip 3D only layer types for 2D mode.
         if (om.serviceType.Equal("SceneLayer") || om.serviceType.Equal("IntegratedMeshLayer") || om.serviceType.Equal("PointCloudLayer")) {
            continue;
         }
         visible = MapThemes.mapServiceExistInMapTheme(mapTheme, om.id); //Check startup map theme for visibility.

      } else { //Skip layers that take too long to display in 3D.
         //if (!Util_App.isMapLayerVisible(om.id, true)) {
         //   continue;
         //}
         if (!Settings.layerIDsFor3D.Contains(om.id) && om.serviceType !== "GroupLayer") {
            continue;
         }
         visible = true; //If the layer is currently visible in 2D, leave it that way in 3D.
      }

      opacity = MapThemes.getMapThemeOpacity(mapTheme, om.id); //Check startup map theme for opacity.
      if (opacity === -1) {
         opacity = null;
         if (om.opacity !== 1) {
            opacity = om.opacity; //Use opacity from the settings.
         }
      }

      if (om.serviceType === "GroupLayer") {
         if (om.mapServiceName === "End") { //Start a group layer.
            groupLayerInProgress = true;
            groupLay = new GroupLayer({ id: om.id, title: om.id, visible: visible });

         } else { //Finish a group layer.
            groupLayerInProgress = false;
            Util.logMsg('Adding group layer: "' + groupLay.title + '"');
            operLayers.Add(groupLay);
         }

      } else {
         props = {
            id: om.id,
            title: om.id,
            opacity: opacity,
            url: om.mapServiceURL,
            visible: visible,
         };

         //Use portal item instead of url.
         if (!om.mapServiceURL.toLowerCase().StartsWith("http")) {
            delete props.url;
            props.portalItem = { id: om.mapServiceURL };
         }

         if (om.serviceType.Equal("MapImageLayer")) {
            const mapImageLayerProps = props as __esri.MapImageLayerProperties;
            if (om.imageFormat) {
               mapImageLayerProps.imageFormat = om.imageFormat as mapImageFormat;
            }
            operLayer = new MapImageLayer(mapImageLayerProps);

         } else if (om.serviceType.Equal("FeatureLayer")) {
            const featureLayerProps = props;

            legendLayer = Legend.getLegendLayerSetting(om.id);
            if (legendLayer) {
               featureLayerProps.legendEnabled = false; //Hide in legend if layer name found.
            }
            if (om.mapServiceURL.EndsWith("FeatureServer")) {
               Util.errMsg("Feature layer not found in REST endpoint: " + om.id);
               continue; //Feature layer index was not found in feature service REST endpoint.
            }
            operLayer = new FeatureLayer(featureLayerProps);

         } else if (om.serviceType.Equal("VectorTileLayer")) {
            const vectorTileLayerProps = props as __esri.VectorTileLayerProperties;
            operLayer = new VectorTileLayer(vectorTileLayerProps);

         } else if (om.serviceType.Equal("TileLayer")) {
            const tileLayerProps = props as __esri.TileLayerProperties;
            operLayer = new TileLayer(tileLayerProps);

         } else if (om.serviceType.Equal("GeoRSSLayer")) {
            const geoRSSLayerProps = props as __esri.GeoRSSLayerProperties;
            operLayer = new GeoRSSLayer(geoRSSLayerProps);

         } else if (om.serviceType.Equal("KMLLayer")) {
            const kmlLayerProps = props as __esri.KMLLayerProperties;
            operLayer = new KMLLayer(kmlLayerProps);

         } else if (om.serviceType.Equal("SceneLayer")) {
            const sceneLayerProps = props as __esri.SceneLayerProperties;
            operLayer = new SceneLayer(sceneLayerProps);

         } else if (om.serviceType.Equal("IntegratedMeshLayer")) {
            const integratedMeshLayerProps = props as __esri.IntegratedMeshLayerProperties;
            operLayer = new IntegratedMeshLayer(integratedMeshLayerProps);

         } else if (om.serviceType.Equal("PointCloudLayer")) {
            const pointCloudLayerProps = props as __esri.PointCloudLayerProperties;
            delete pointCloudLayerProps.opacity;
            operLayer = new PointCloudLayer(pointCloudLayerProps);

         } else if (om.serviceType.Equal("WMSLayer")) {
            const wmsLayerProps = props as __esri.WMSLayerProperties;
            operLayer = new WMSLayer(wmsLayerProps);

         } else if (om.serviceType.Equal("WMTSLayer")) {
            const wmtsLayerProps = props as __esri.WMTSLayerProperties;
            operLayer = new WMTSLayer(wmtsLayerProps);
         }

         //Set custom layer properties.
         Custom.setLayerProps(operLayer);

         //Operational layer is ready.
         operLayer.when(function (layer: __esri.Layer) {
            Util.logMsg('Operational layer is ready: "' + layer.id + '"');

            //Process feature layer.
            if (layer.type === "feature") {
               const featureLayer = layer as __esri.FeatureLayer;

               //Report editing security risk.
               if (featureLayer.capabilities.operations.supportsEditing) {
                  Util.errMsg("Feature layer supports editing!: " + layer.id);
               }

               //Remove fields not used for dynamic labeling.
               if (featureLayer.fields) {
                  //let keepFields: __esri.Field[] = [];

                  //for (const field of featureLayer.fields) {
                  //   if (!field.editable || field.type === "oid" || field.type === "geometry" ||
                  //      field.name.toLowerCase() === featureLayer.displayField.toLowerCase() ||
                  //      field.name === "Area") {
                  //      keepFields.Add(field);
                  //   }
                  //}

                  //featureLayer.fields = keepFields;
                  //Util.logMsg("Keep Fields for " + layer.id + ": ", keepFields);

                  //let keepFieldNames: string[] = [];

                  //for (const field of featureLayer.fields) {
                  //   if (!field.editable || field.type === "oid" || field.type === "geometry" ||
                  //      field.name.toLowerCase() === featureLayer.displayField.toLowerCase() ||
                  //      field.name === "Area") {
                  //      keepFieldNames.Add(field.name);
                  //   }
                  //}

                  //featureLayer.outFields = keepFieldNames;
                  //Util.logMsg("Keep Field names for " + layer.id + ": ", keepFieldNames);

                  // Test if this makes panning faster.
                  //featureLayer.outFields = [featureLayer.objectIdField];

                  //Load coded value domains for feature layers.  Subtypes are only available at the REST endpoint.
                  for (const field of featureLayer.fields) {
                     if (field.domain) {
                        const domain = field.domain as __esri.CodedValueDomain;

                        if (domain.codedValues && domain.codedValues.length > 0) {
                           //   if (typeof field.domain.codedValues[0].code === "number") {
                           const codedValue: Settings.iCodedValue = { layerName: layer.id, fieldName: field.name, values: [] };

                           for (const value of domain.codedValues) {
                              codedValue.values.Add({ code: value.code, name: value.name });
                           }
                           Settings.codedValues.Add(codedValue);
                        }
                     }
                  }
               }

            //Process map image service.
            } else if (layer.type === "map-image" || layer.type === "tile") {
               const mapImageLayer = layer as __esri.MapImageLayer;

               if (mapImageLayer.sublayers && mapImageLayer.sublayers.length > 0) {

                  //Set sublayer id's in settings.
                  Util_App.setSublayerIndexesInSettings(mapImageLayer);

                  //Turn on sublayers in the default map theme.
                  subLayersOn = MapThemes.getMapThemeVisibleSublayers(mapTheme, layer.id);
                  MapThemes.setSublayerVisibility(mapImageLayer.sublayers, subLayersOn);

                  //Turn off map image service sublayers in legend.
                  legendLayer = Legend.getLegendLayerSetting(layer.id);
                  if (legendLayer) {
                     Legend.hideLegendSublayers(mapImageLayer.sublayers, legendLayer.sublayerNames);
                  }
               }
            }

         }).catch(function (error: Error) { //Trap for errors.
            Util.errMsg("Operational layer failed to load.", error);
         });

         Util.logMsg('Adding operational layer: "' + operLayer.id + '"');
         if (groupLayerInProgress) {
            groupLay.layers.add(operLayer);
         } else {
            operLayers.Add(operLayer);
         }

      } //end if group layer or not.
   } //end for all operational map settings.

   //return myGroupLayer;
   return operLayers;
}

//Load ground elevation.
function loadGround(): string | __esri.Ground {
   const elevationLayers: __esri.ElevationLayer[] = [];
   let elevationLayer: __esri.ElevationLayer;

   let isWebMercator: boolean = Util_App.isWebMercator();

   for (const em of Settings.elevMaps) {
      if (!isWebMercator && !em.isGlobal || isWebMercator && em.isGlobal) {
         elevationLayer = new ElevationLayer({
            id: em.id,
            url: em.mapServiceURL,
            visible: true, //Required to be true.
         });

         //Trap for errors.
         elevationLayer.when().catch(function (error: Error) {
            Util.errMsg("Elevation layer failed to load.", error);
         });

         Util.logMsg('Adding elevation layer: "' + elevationLayer.id + '"');
         elevationLayers.Add(elevationLayer);
      }
   }

   let ground: string | __esri.Ground = "world-elevation";
   addElevationLayers = null;

   if (elevationLayers.length > 0) {
      if (isWebMercator) {
         addElevationLayers = elevationLayers;
      } else {
         ground = new Ground({ layers: elevationLayers });
      }
   }
   return ground;
}

//Create 2D map view.
function createMapView(mapExtent: __esri.Extent) {
      Util.logMsg('Creating map view.');

      //Compute map extent.
      let extent = mapExtent; //Use input argument.
      if (!extent) {
         extent = mapExtentStart; //User settings.
      }

      //Create map view.
      MainMap.activeViewDiv.innerHTML = ""; //Clear the map container.

      let maxScale: number = 0; //450 Custom!

      let basemapType: string = startupBasemap.baseLayers.getItemAt(0).type;

      if (basemapType !== "vector-tile") {
         maxScale = 0; //Custom!
      }

      mapView = new MapView({
         container: MainMap.activeViewDiv,
         map: MainMap.map,
         constraints: { rotationEnabled: true, snapToZoom: true, maxScale: maxScale },
         highlightOptions: {
            color: Settings.hoverPolygonSym.color,
            haloColor: Settings.hoverPolygonSym.outline.color,
         },
      });

      activeView = MainMap.mapView;

      //Remove initial attribution so it can be added as a widget.
      MainMap.mapView.ui.remove("attribution");

      //Set map extent.
      if (extent) {
         MainMap.mapView.extent = extent; // Use "extent Full" instead for animation on startup;
      }

      //Map view is ready.
      MainMap.mapView.when(function () {
         Util.logMsg("Map view is ready");
         readyForRefresh2D = true;
         MainMap.map.ground.load(); //Force the ground elevation to load since it does not for some reason.

         //Add or remove 3D button when basemap changes.
         MainMap.mapView.watch("spatialReference", function (newspatialReference, oldspatialReference) {
            //Util.errMsg("MainMap -> Is Mercator: " + MainMap.mapView.spatialReference.isWebMercator);
            MainToolbar.addOrRemove3D();
            MainToolbar.render();
         });

         reactiveUtils.when(() => MainMap.mapView?.navigating === true, function () {
            QueryString.setZoomToStartExtent(false);
         });

         if (Settings.doubleBasemapTileLevels) {
            addNewLODs(); //Double the number of basemap tile levels of detail.
         }

         Highlight.createIdentifyTasks();
         HighlightGraphics.init();
         ScaleDependentRender.init();

         Widgets.createLocateMe(MainMap.mapView);
         Widgets.createHome(MainMap.mapView);
         Widgets.createCompass(MainMap.mapView);
         Widgets.createBasemapToggle();
         Widgets.createCoordWidget();
         Widgets.createScalebar();

         PrevNextExtent.createPrevNextButtons();
         PrevNextExtent.init();

         Widgets.setPosition(); //Set the widget positions on website startup part 3.

         //Get coded value domains for map image services.
         setTimeout(function () { CodedValueDomains.getMapImageCodedValueDomains(); }, 1000);

      }).catch(function (error: Error) { //Trap for errors.
         Util.errMsg('Unable to create map view.', error);
      });

      //Refresh mapview after first map service is rendered.
      reactiveUtils.whenOnce(() => !MainMap.mapView.updating).then(() => {

         if (readyForRefresh2D && MainMap.mapView.ready) {
            Util.logMsg('Updating the mapView');
            readyForRefresh2D = false;

            toggleViewDivOpacity();
            if (MainMap.sceneView) {
               MainMap.sceneView.container = null;
            }

            //Add graphics layers to map view.
            MainMap.map.addMany(graphicsLayers);

            if (MainMap.startUpInProgress) {
               startUpInProgress = false;
               Util.logMsg('Zooming to 2D full extent.');

               if (mapExtentQS) {
                  MainMap.mapView.goTo(mapExtentQS, { duration: 1500 });
                  mapExtentQS = null;

               } else if (extent && QueryString.zoomToStartExtent) {
                  MainMap.mapView.goTo(extent, { duration: 1500 });
               }

            } else {
               Util.logMsg('Refreshing mapView.');
               setTimeout(function () { MainMap.mapView.goTo(MainMap.mapView.viewpoint); }, 100);
            }

            Widgets.setPosition(); //Set the widget positions on website startup part 2.

            if (DrawerMenu.classRef) {
               DrawerMenu.classRef.refresh();
            }
            if (MainToolbar) {
               MainToolbar.render();
            }

            reBuildMapInProgress = false;
            FeatureLayerLabels.init();
            MapTips.initMapTips();

            hideBusy();
         }
      });
} //end create map view.

//Create the scene view.
function createSceneView(mapExtent: __esri.Extent) {
   import("@arcgis/core/views/SceneView").then(({ default: SceneView } ) => {

      Util.logMsg('Creating scene view.');
      MainMap.activeViewDiv.innerHTML = ""; //Clear the map container.

      sceneView = new SceneView({
         container: MainMap.activeViewDiv,
         map: MainMap.map,
         viewingMode: "local",
         //qualityMode: "medium",
         //environment: { atmosphere: null, starsEnabled: false },
         //clippingArea: mapExtent,
         highlightOptions: {
            color: Settings.hoverPolygonSym3D.color,
            haloColor: Settings.hoverPolygonSym3D.outline.color,
         },
         extent: mapExtent,
      });

      activeView = MainMap.sceneView;

      //Remove initial attribution so it can be added as a widget.
      MainMap.sceneView.ui.remove("attribution");

      //Set map extent.
      //const extent = mapExtent;
      //MainMap.sceneView.extent = extent;

      //Scene view is ready.
      MainMap.sceneView.when(function () {
         Util.logMsg("SceneView is ready.");

         //Work around bugs.
         if (MainMap.sceneView.scale < 4800) {
            Util.logMsg('Forcing sceneView scale to 4800.');
            MainMap.sceneView.scale = 4800;
         }

         Highlight.createIdentifyTasks();
         HighlightGraphics.init();

         Widgets.createLocateMe(MainMap.sceneView);
         Widgets.createHome(MainMap.sceneView);
         Widgets.createBasemapToggle();
         Widgets.createCoordWidget();

      }).catch(function (error: Error) { //Trap for errors.
         Util.errMsg("SceneView failed to initialize.", error);
      });

      //Refresh the scene view after the first map service is rendered.
      reactiveUtils.whenOnce(() => !MainMap.sceneView.updating).then(() => {

         Util.logMsg('Updating sceneView is complete');

         if (MainMap.sceneView.ready) {

            toggleViewDivOpacity();
            MainMap.mapView.container = null;

            if (MainMap.startUpInProgress) {
               startUpInProgress = false;
               Util.logMsg('Zooming to 3D full extent and tilting.');

               if (mapExtent) {
                  MainMap.sceneView.goTo(mapExtent).then(function () {
                     setTimeout(function () { MainMap.sceneView.goTo({ tilt: 60 }); }, 100);
                  });
               }

            } else {
               Util.logMsg('Tilting the sceneView');
               MainMap.sceneView.goTo({ tilt: 60 }, { duration: 800 });
               Widgets.setPosition();
               DrawerMenu.classRef.refresh();
               MainToolbar.render();
            }

            //Add graphics layers to scene view.
            MainMap.map.addMany(graphicsLayers);

            reBuildMapInProgress = false;
            FeatureLayerLabels.init();
            MapTips.initMapTips();

            //Util.errMsg("3D Performance Info", MainMap.sceneView.performanceInfo);
            hideBusy();
         }
      });

      //Check for Web GL support.
      let webGL_supported = true;

      MainMap.sceneView.when().catch(function (error: Error) {
         webGL_supported = false;
         Util.errMsg('Unable to create scene view. WebGL may not be supported in this browser.', error);

         //Switch to 2D mode if 3D is not supported.
         view2D = true;
         MainMap.activeViewDiv.innerHTML = ""; //Clear the map container.
         setTimeout(function () { buildMap(); }, 1000);
      });

   }).catch((error: Error) => {
      Util.errMsg('An error occurred while loading the SceneView module:', error);
   });

} //end create scene view.

//Toggle between 2D and 3D.
export function toggle_2D_3D() {
   if (busy) {
      return;
   }

   showBusy();
   DrawerMenu.closeTool();

   // Destroy tools that never get unmounted.
   BasemapGallery.destroy();
   ElevationProfile.destroy();
   MapLayers.destroy();

   //Change to 3D view.
   if (MainMap.view2D) {
      Util.logMsg('Changing to 3D mode.');
      view2D = !MainMap.view2D; //Toggle the view type.
      Widgets.removeScalebar();

      //Work around bugs.
      const viewPoint = MainMap.mapView.viewpoint.clone();

      if (Util_App.isWebMercator()) {
         if (viewPoint.scale < 1200) {
            Util.logMsg('Setting global viewPoint scale to: 1200');
            viewPoint.scale = 1200;
         }

      } else {
         if (viewPoint.scale < 3000) {
            Util.logMsg('Setting local viewPoint scale to: 3000');
            viewPoint.scale = 3000;
         }
      }

      //MainMap.sceneView.viewpoint = viewPoint;
      toggleViewDiv();

      const mapExtent = MainMap.mapView.extent.clone();
      buildMap(mapExtent); //Rebuild the map.

   } else { //Change to 2D view.
      Util.logMsg('Changing to 2D mode.');
      view2D = !MainMap.view2D; //Toggle the view type.

      MainMap.sceneView.goTo({ tilt: 0 }, { duration: 800 }).then(function () {
         //MainMap.mapView.viewpoint = MainMap.sceneView.viewpoint.clone();
         toggleViewDiv();

         const mapExtent3D = MainMap.sceneView.extent.clone();
         buildMap(mapExtent3D); //Rebuild the map.
      });
   }
}

//Toggle the active view div.
function toggleViewDiv() {
   Util.logMsg('Toggling view div.');

   if (MainMap.activeViewDiv.id === viewDiv1.id) {
      activeViewDiv = viewDiv2;
      oldViewDiv = viewDiv1;
   } else {
      activeViewDiv = viewDiv1;
      oldViewDiv = viewDiv2;
   }
   Util.logMsg('Active container div set to: ' + MainMap.activeViewDiv.id);

   MainMap.activeViewDiv.innerHTML = ""; //Clear the map container.
   MainMap.activeViewDiv.style.opacity = ".01";
   MainMap.activeViewDiv.style.visibility = "visible";
}

//Toggle the active view div opacity.
function toggleViewDivOpacity() {
   Util.logMsg('Toggling view div opacity.');
   oldViewDiv.style.visibility = "hidden";
   MainMap.activeViewDiv.style.opacity = "1";
}

//Double the number of basemap tile levels of detail.
function addNewLODs() {
   let i: number;
   let level = -1;
   let newLOD: __esri.LOD;
   const newLODs: __esri.LOD[] = [];
   const curLODs = MainMap.mapView.constraints.effectiveLODs;

   if (!curLODs) {
      return; //Quit if basemap is an imageryLayer.
   }

   for (i = 0; i < curLODs.length - 1; i++) {
      const curLOD = curLODs[i];
      const nextLOD = curLODs[i + 1];

      newLOD = JSON.parse(JSON.stringify(curLOD)) as __esri.LOD; //Clone it.
      level++;
      newLOD.level = level;
      newLODs.Add(newLOD);

      newLOD = JSON.parse(JSON.stringify(curLOD)) as __esri.LOD; //Clone it.
      level++;
      newLOD.level = level;
      newLOD.resolution = (curLOD.resolution + nextLOD.resolution) / 2;
      newLOD.scale = (curLOD.scale + nextLOD.scale) / 2;
      newLODs.Add(newLOD);
   }

   //Flush out the last one.
   newLOD = JSON.parse(JSON.stringify(curLODs[curLODs.length - 1])) as __esri.LOD; //Clone it.
   level++;
   newLOD.level = level;
   newLODs.Add(newLOD);

   //Add large scale (zoomed-in) levels.
   for (i = 0; i < 2; i++) {
      const nextRes: number = newLOD.resolution / 2;
      const nextScale: number = newLOD.scale / 2;

      const middleRes = (newLOD.resolution + nextRes) / 2;
      const middleScale = (newLOD.scale + nextScale) / 2;

      newLOD = JSON.parse(JSON.stringify(newLOD)) as __esri.LOD; //Clone it.
      level++;
      newLOD.level = level;
      newLOD.resolution = middleRes;
      newLOD.scale = middleScale;
      newLODs.Add(newLOD);

      newLOD = JSON.parse(JSON.stringify(newLOD)) as __esri.LOD; //Clone it.
      level++;
      newLOD.level = level;
      newLOD.resolution = nextRes;
      newLOD.scale = nextScale;
      newLODs.Add(newLOD);
   }

   //Update the map views zoom levels.
   MainMap.mapView.constraints.lods = newLODs;
   MainMap.mapView.constraints.minScale = MainMap.mapView.constraints.effectiveMinScale;
   MainMap.mapView.constraints.minZoom = 0;
   MainMap.mapView.constraints.maxZoom = level;

   //Util.errMsg("Original LODs: ", curLODs);
   //Util.errMsg("New LODs: ", newLODs);
   //Util.errMsg("Constraints: ", MainMap.mapView.constraints);
}

//Initialize map exents.
function initMapExtents() {
   Util.logMsg('Initializing map extents');

   mapExtentStart = getMapExtent(Settings.mapExtentStart, Settings.wkID);
   mapExtentFull = getMapExtent(Settings.mapExtentFull, Settings.wkID);

   //Get map extent query string.
   mapExtentQS = getMapExtent(QueryString.mapExtent, Settings.wkID);
}

//Get map extent from comma delimmited string.
export function getMapExtent(str: string, wkid?: number): __esri.Extent {
   let mapExtent: __esri.Extent = null;

   if (str) {
      const sa: string[] = str.split(",");

      if (sa.length === 4 && wkid) {
         mapExtent = new Extent({
            xmin: Util.parseFlt(sa[0]),
            ymin: Util.parseFlt(sa[1]),
            xmax: Util.parseFlt(sa[2]),
            ymax: Util.parseFlt(sa[3]),
            spatialReference: { wkid: wkid },
         });

         //WKID is the last argument.
      } else if (sa.length === 5) {
         mapExtent = new Extent({
            xmin: Util.parseFlt(sa[0]),
            ymin: Util.parseFlt(sa[1]),
            xmax: Util.parseFlt(sa[2]),
            ymax: Util.parseFlt(sa[3]),
            spatialReference: { wkid: Util.parseInteger(sa[4]) },
         });
      }
   }
   return mapExtent;
}

function showBusy() {
   busy = true;
   if (Spinner) {
      Spinner.show();
   }
}

function hideBusy() {
   if (Spinner) {
      Spinner.hide();
   }
   busy = false;
}
